import executeRequest, { RequestOutput, APICurrency } from 'app/api'
import { APIPaginatable, APIPaginatableResponse, APISortable, APISearchable } from 'app/api/types'


//Endpoints

enum APIEndpoint {
  GetAllProducts = 'api/products',
  GetAllProductsTaxonomies = 'api/products/getTaxonomies',
  CreateProductTaxonomy = 'api/products/createTaxonomy',
  GetProductById = 'api/products/getById',
  CreateProduct = 'api/products/create',
  UpdateProduct = 'api/products/update',
  DeleteProduct = 'api/products/delete',
  UpdateProductStatus = 'api/products/updatePublished',
  GetProductTranslation = 'api/products/getTranslation',
  UpdateProductTranslation = 'api/products/updateTranslation',
  GetProductPrices = 'api/products/getPrices',
  UpdateProductPrices = 'api/products/updatePrices',
  GetProductStock = 'api/products/getStock',
  UpdateProductStock = 'api/products/updateStock',
  GetProductDimensions = 'api/products/getDimensions',
  UpdateProductDimensions = 'api/products/updateDimensions',
  GetProductGallery = 'api/products/getGallery',
  AddProductGalleryImage = 'api/products/addGalleryImage',
  RemoveProductGalleryImage = 'api/products/removeGalleryImage',
  GetAllProductArtists = 'api/products/getArtists',
  AddArtistToProduct = 'api/products/addArtist',
  RemoveArtistFromProduct = 'api/products/removeArtist',
  GetProductPickupAddress = 'api/products/getPickupAddress',
  AddProductPickupAddress = 'api/products/addPickupAddress',
  RemoveProductPickupAddress = 'api/products/removePickupAddress',
  GetProductChildProducts = 'api/products/getChilds',
  AddChildProductToProduct = 'api/products/addChild',
  RemoveChildProductFromProduct = 'api/products/removeChild',
}



//Types

export type APIProduct = {
  id: number
  keyword: string
  published: boolean | null
  sku: string
  featured?: boolean | null
  featuredOrder?: number
  pageId: number
  parentId: number | null
  tokenized: boolean
  onEnquiry: boolean | null
  year: number
  translation: APIProductTranslation[]
  prices: APIProductPrice[]
  dimensions: APIProductDimension,
  stock: APIProductStock,
  mainImage: APIProductImage,
  taxonomies: {
    id: number
    name: string
    slug: string
  }[]
}

export type APIProductTranslation = {
  name: string
  description?: string
  id: number,
  slug: string,
  ingredients: string,
  benefits: string,
  deepening: string,
  languageId: number
}
export type APIProductImage = {
  id: number
  type: string
  folder: number
  filename: string
  basepath: string
  path: string
}
export type APIProductTaxonomy = {
  id: number
  keyword: string
  pageId: number
  pageTypeId: number
  parentId: number
  published: boolean
  translation: {
    name: string
  }[]
}
export type APIProductStock = {
  outOfStock: boolean
  quantity: number
  maximalQuantity: number
  minimalQuantity: number
}
export type APIProductDimension = {
  width: number
  height: number
  depth: number
  weight: number
}
export type APIProductPrice = {
  id: number
  price: number
  sellingPrice: number
  wholesalePrice: number
  unitPrice: number
  unityCommonStringId: number
  currency: APICurrency
}



//Requests

export interface GetAllProductsRequestParams extends APIPaginatable, APISortable, APISearchable {
  languageId: number
}
export interface GetAllProductsResponse extends APIPaginatableResponse {
  products: APIProduct[]
}
enum GetAllProductsResponseError {
}
export async function getAllProducts(params: GetAllProductsRequestParams, output?: RequestOutput<GetAllProductsResponse, GetAllProductsResponseError>) {
  executeRequest({ endpoint: APIEndpoint.GetAllProducts, params: params, output: output })
}


export interface GetAllProductsTaxonomiesRequestParams extends APIPaginatable, APISortable, APISearchable {
  languageId: number
}
export interface GetAllProductsTaxonomiesResponse extends APIPaginatableResponse {
  taxonomies: APIProductTaxonomy[]
}
enum GetAllProductsTaxonomiesResponseError {
}
export async function getAllProductsTaxonomies(params: GetAllProductsTaxonomiesRequestParams, output?: RequestOutput<GetAllProductsTaxonomiesResponse, GetAllProductsTaxonomiesResponseError>) {
  executeRequest({ endpoint: APIEndpoint.GetAllProductsTaxonomies, params: params, output: output })
}


export type CreateProductTaxonomyRequestParams = {
  parentId: number | null
}
type CreateProductTaxonomyResponse = {
  taxonomy: {
    id: number
  }
}
enum CreateProductTaxonomyReponseError {
}
export async function createProductTaxonomy(params: CreateProductTaxonomyRequestParams, output?: RequestOutput<CreateProductTaxonomyResponse, CreateProductTaxonomyReponseError>) {
  executeRequest({ endpoint: APIEndpoint.CreateProductTaxonomy, params: params, output: output })
}


export type GetProductRequestParams = {
  id: number
}
export type GetProductResponse = {
  product: APIProduct
}
enum GetProductResponseError {
}
export async function getProduct(params: GetProductRequestParams, output?: RequestOutput<GetProductResponse, GetProductResponseError>) {
  executeRequest({ endpoint: APIEndpoint.GetProductById, params: params, output: output })
}


export type CreateProductRequestParams = {
  parentId: number | null
  tags: number[]
}
type CreateProductResponse = {
  product: {
    id: number
    name: string
  }
}
enum CreateProductReponseError {
}
export async function createProduct(params: CreateProductRequestParams, output?: RequestOutput<CreateProductResponse, CreateProductReponseError>) {
  executeRequest({ endpoint: APIEndpoint.CreateProduct, params: params, output: output })
}


export type UpdateProductRequestParams = {
  id: number
  keyword: string
  parentId: number | null
  sku: string
  onEnquiry: boolean | null
  featured?: boolean | null
  featuredOrder?: number
  year: number
}
type UpdateProductResponse = {
}
enum UpdateProductReponseError {
}
export async function updateProduct(params: UpdateProductRequestParams, output?: RequestOutput<UpdateProductResponse, UpdateProductReponseError>) {
  executeRequest({ endpoint: APIEndpoint.UpdateProduct, params: params, output: output })
}


export type DeleteProductRequestParams = {
  id: number
}
type DeleteProductResponse = {
}
enum DeleteProductReponseError {
}
export async function deleteProduct(params: DeleteProductRequestParams, output?: RequestOutput<DeleteProductResponse, DeleteProductReponseError>) {
  executeRequest({ endpoint: APIEndpoint.DeleteProduct, params: params, output: output })
}


export type UpdateProductStatusRequestParams = {
  id: number
  published: boolean,
}
type UpdateProductStatusResponse = {
}
enum UpdateProductStatusReponseError {
}
export async function updateProductStatus(params: UpdateProductStatusRequestParams, output?: RequestOutput<UpdateProductStatusResponse, UpdateProductStatusReponseError>) {
  executeRequest({ endpoint: APIEndpoint.UpdateProductStatus, params: params, output: output })
}


export type GetProductTranslationRequestParams = {
  productId: number
  languageId: number
}
export type GetProductTranslationResponse = {
  productTranslation: APIProductTranslation
}
enum GetProductTranslationResponseError {
}
export async function getProductTranslation(params: GetProductTranslationRequestParams, output?: RequestOutput<GetProductTranslationResponse, GetProductTranslationResponseError>) {
  executeRequest({ endpoint: APIEndpoint.GetProductTranslation, params: params, output: output })
}


export type UpdateProductTranslationRequestParams = {
  id: number,
  name?: string | null,
  slug?: string | null,
  description?: string | null,
  ingredients?: string | null,
  benefits?: string | null,
  deepening?: string | null,
}
type UpdateProductTranslationResponse = {
}
enum UpdateProductTranslationReponseError {
}
export async function updateProductTranslation(params: UpdateProductTranslationRequestParams, output?: RequestOutput<UpdateProductTranslationResponse, UpdateProductTranslationReponseError>) {
  executeRequest({ endpoint: APIEndpoint.UpdateProductTranslation, params: params, output: output })
}



export type GetProductPricesRequestParams = {
  productId: number
}

export type GetProductPricesResponse = {
  productPrices: {
    id: number,
    name: string,
    slug: string,
    description: string,
    meta: {
      desktop: {
        metaTitleDesktop: string | null
        metaDescriptionDesktop: string | null,
      },
      mobile: {
        metaTitleMobile: string | null,
        metaDescriptionMobile: string | null,
      }
    }
    languageId: number
  }
}

enum GetProductPricesResponseError {
}

export async function getProductPrices(params: GetProductPricesRequestParams, output?: RequestOutput<GetProductPricesResponse, GetProductPricesResponseError>) {
  executeRequest({
    endpoint: APIEndpoint.GetProductPrices,
    params: params,
    output: output,
  })
}



export type UpdateProductPricesRequestParams = {
  productId: number,
  currencyId: number
  isMarginScheme: boolean | null
  price: number | null,
  sellingPrice: number | null,
  wholesalePrice: number | null,
  unitPrice: number | null
  unityCommonStringId?: number | null
}

type UpdateProductPricesResponse = {
}

enum UpdateProductPricesReponseError {
}

export async function updateProductPrices(params: UpdateProductPricesRequestParams, output?: RequestOutput<UpdateProductPricesResponse, UpdateProductPricesReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.UpdateProductPrices,
    params: params,
    output: output,
  })
}




export type GetProductStockRequestParams = {
  productId: number
}

export type GetProductStockResponse = {
  productStock: {
    id: number,
    name: string,
    slug: string,
    description: string,
    meta: {
      desktop: {
        metaTitleDesktop: string | null
        metaDescriptionDesktop: string | null,
      },
      mobile: {
        metaTitleMobile: string | null,
        metaDescriptionMobile: string | null,
      }
    }
    languageId: number
  }
}

enum GetProductStockResponseError {
}

export async function getProductStock(params: GetProductStockRequestParams, output?: RequestOutput<GetProductStockResponse, GetProductStockResponseError>) {
  executeRequest({
    endpoint: APIEndpoint.GetProductStock,
    params: params,
    output: output,
  })
}



export type UpdateProductStockRequestParams = {
  productId: number,
  quantity: number,
  minimalQuantity: number,
  maximalQuantity: number,
  outOfStock: boolean
}

type UpdateProductStockResponse = {
}

enum UpdateProductStockReponseError {
}

export async function updateProductStock(params: UpdateProductStockRequestParams, output?: RequestOutput<UpdateProductStockResponse, UpdateProductStockReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.UpdateProductStock,
    params: params,
    output: output,
  })
}




export type GetProductDimensionsRequestParams = {
  productId: number
}

export type GetProductDimensionsResponse = {
  productDimensions: {
    id: number,
    name: string,
    slug: string,
    description: string,
    meta: {
      desktop: {
        metaTitleDesktop: string | null
        metaDescriptionDesktop: string | null,
      },
      mobile: {
        metaTitleMobile: string | null,
        metaDescriptionMobile: string | null,
      }
    }
    languageId: number
  }
}

enum GetProductDimensionsResponseError {
}

export async function getProductDimensions(params: GetProductDimensionsRequestParams, output?: RequestOutput<GetProductDimensionsResponse, GetProductDimensionsResponseError>) {
  executeRequest({
    endpoint: APIEndpoint.GetProductDimensions,
    params: params,
    output: output,
  })
}



export type UpdateProductDimensionsRequestParams = {
  productId: number,
  width: number | null,
  height: number | null,
  depth: number | null,
  weight: number | null
}

type UpdateProductDimensionsResponse = {
}

enum UpdateProductDimensionsReponseError {
}

export async function updateProductDimensions(params: UpdateProductDimensionsRequestParams, output?: RequestOutput<UpdateProductDimensionsResponse, UpdateProductDimensionsReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.UpdateProductDimensions,
    params: params,
    output: output,
  })
}




export interface GetAllProductArtistsRequestParams {
  productId: number
  languageId: number
}
export interface GetAllProductArtistsResponse {
  artists: {
    id: number
    keyword: string
    published: boolean | null
    pageId: number
    parentId: number | null
    mainImage: {
      id: number
      type: string
      folder: number
      filename: string
      basepath: string
      path: string
    }
    year: number | null
    translation: {
      name: string
      shortDescription: string
    }[],
  }[]
}

enum GetAllProductArtistsResponseError {
}

export async function getAllProductArtists(params: GetAllProductArtistsRequestParams, output?: RequestOutput<GetAllProductArtistsResponse, GetAllProductArtistsResponseError>) {
  executeRequest({
    endpoint: APIEndpoint.GetAllProductArtists,
    params: params,
    output: output,
  })
}



//Retrieve the authentication token.
//Used when logging in and authorizing the client to make future API calls.

export type AddArtistToProductRequestParams = {
  productId: number
  artistId: number
}

export type AddArtistToProductResponse = {
}

enum AddArtistToProductReponseError {
}

export async function addArtistToProduct(params: AddArtistToProductRequestParams, output?: RequestOutput<AddArtistToProductResponse, AddArtistToProductReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.AddArtistToProduct,
    params: params,
    output: output,
  })
}


//Retrieve the authentication token.
//Used when logging in and authorizing the client to make future API calls.

export type RemoveArtistFromProductRequestParams = {
  productId: number
  artistId: number
}

export type RemoveArtistFromProductResponse = {
}

enum RemoveArtistFromProductReponseError {
}

export async function removeArtistFromProduct(params: RemoveArtistFromProductRequestParams, output?: RequestOutput<RemoveArtistFromProductResponse, RemoveArtistFromProductReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.RemoveArtistFromProduct,
    params: params,
    output: output,
  })
}




export type GetProductPickupAddressRequestParams = {
  productId: number,
}

export type GetProductPickupAddressResponse = {
  pickupAddress: {
    id: number
    description: string
    convelioAddressId: number
  }[]
}

enum GetProductPickupAddressReponseError {
}

export async function getProductPickupAddress(params: GetProductPickupAddressRequestParams, output?: RequestOutput<GetProductPickupAddressResponse, GetProductPickupAddressReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.GetProductPickupAddress,
    params: params,
    output: output,
  })
}



export type AddProductPickupAddressRequestParams = {
  productId: number,
  address: string
}

export type AddProductPickupAddressResponse = {
  pickupAddress: {
    id: number
    description: string
    convelioAddressId: number
  }
}

enum AddProductPickupAddressReponseError {
}

export async function addProductPickupAddress(params: AddProductPickupAddressRequestParams, output?: RequestOutput<AddProductPickupAddressResponse, AddProductPickupAddressReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.AddProductPickupAddress,
    params: params,
    output: output,
  })
}



export type RemoveProductPickupAddressRequestParams = {
  productId: number,
}

export type RemoveProductPickupAddressResponse = {
}

enum RemoveProductPickupAddressReponseError {
}

export async function removeProductPickupAddress(params: RemoveProductPickupAddressRequestParams, output?: RequestOutput<RemoveProductPickupAddressResponse, RemoveProductPickupAddressReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.RemoveProductPickupAddress,
    params: params,
    output: output,
  })
}


export interface GetProductGalleryRequestParams {
  productId: number
}

export interface GetProductGalleryResponse {
  productGallery: {
    id: number
    itemOrder: number
    image: {
      id: number
      filename: string,
      type: string,
      basepath: string
      folder: number
    }
  }[]
}

enum GetProductGalleryResponseError {
}

export async function getProductGallery(params: GetProductGalleryRequestParams, output?: RequestOutput<GetProductGalleryResponse, GetProductGalleryResponseError>) {
  executeRequest({
    endpoint: APIEndpoint.GetProductGallery,
    params: params,
    output: output,
  })
}



export interface AddProductGalleryImageRequestParams {
  productId: number
  mediaFileId: number
}

export interface AddProductGalleryImageResponse {
}

enum AddProductGalleryImageResponseError {
}

export async function addProductGalleryImage(params: AddProductGalleryImageRequestParams, output?: RequestOutput<AddProductGalleryImageResponse, AddProductGalleryImageResponseError>) {
  executeRequest({
    endpoint: APIEndpoint.AddProductGalleryImage,
    params: params,
    output: output,
  })
}



export interface RemoveProductGalleryImageRequestParams {
  id: number
}

export interface RemoveProductGalleryImageResponse {
}

enum RemoveProductGalleryImageResponseError {
}

export async function removeProductGalleryImage(params: RemoveProductGalleryImageRequestParams, output?: RequestOutput<RemoveProductGalleryImageResponse, RemoveProductGalleryImageResponseError>) {
  executeRequest({
    endpoint: APIEndpoint.RemoveProductGalleryImage,
    params: params,
    output: output,
  })
}


export type GetProductChildProductsRequestParams = {
  productId: number
  languageId: number
}
export type GetProductChildProductsResponse = {
  childProducts: APIProduct[]
}
enum GetProductChildProductsReponseError {
}
export async function getProductChildProducts(params: GetProductChildProductsRequestParams, output?: RequestOutput<GetProductChildProductsResponse, GetProductChildProductsReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.GetProductChildProducts,
    params: params,
    output: output,
  })
}



export type AddChildProductToProductRequestParams = {
  productId: number
  childProductId: number
}
export type AddChildProductToProductResponse = {
  childProduct: APIProduct
}
enum AddChildProductToProductReponseError {
}
export async function addChildProductToProduct(params: AddChildProductToProductRequestParams, output?: RequestOutput<AddChildProductToProductResponse, AddChildProductToProductReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.AddChildProductToProduct,
    params: params,
    output: output,
  })
}



export type RemoveChildProductFromProductRequestParams = {
  productId: number
  childProductIds: number[]
}
export type RemoveChildProductFromProductResponse = {
  childProduct: APIProduct
}
enum RemoveChildProductFromProductReponseError {
}
export async function removeChildProductFromProduct(params: RemoveChildProductFromProductRequestParams, output?: RequestOutput<RemoveChildProductFromProductResponse, RemoveChildProductFromProductReponseError>) {
  executeRequest({
    endpoint: APIEndpoint.RemoveChildProductFromProduct,
    params: params,
    output: output,
  })
}